/**
 * Retrieval for help mode.
 *
 * Help mode answers from the published docs and nothing else, so this is the entire
 * universe a help answer may draw on. It runs against the same manifest the ⌘K palette
 * searches, through the same scorer — the third consumer of `search/scoring`, and the
 * reason that module is worth having: retrieval and search ranking the same corpus
 * differently would mean the assistant cites pages the search box will not show you.
 *
 * ── Why this used to be broken (VRG-227) ──────────────────────────────────────────────
 * `search-index.json` carries exactly ONE excerpt per document — its lead paragraph — and
 * headings as `{id, text, depth, line}`, a line number with no body text attached. Every
 * match this module produced, whether it matched the doc's title or a specific heading deep
 * in the page, was handed the SAME lead-paragraph excerpt. So a question like "how do I
 * transfer a certificate" surfaced four context entries — four different headings — all
 * carrying the identical intro paragraph, none of which mentions transferring at all. The
 * model was answering correctly ("the docs don't cover this") against an INPUT that never
 * contained the answer; the retrieval was the defect, not the model.
 *
 * The fix (in `docSection.ts`): when a match is heading-level, load the guide's full body
 * (lazily — see below) and slice out that heading's own section, bounded by the NEXT heading
 * in document order (whatever its depth) or end-of-document. A page-level match (no heading —
 * the query hit the title/tags/excerpt fields themselves) keeps using the lead excerpt, which
 * is what a "here is this whole guide" result ought to show.
 *
 * ── Staying lazy ───────────────────────────────────────────────────────────────────────
 * Guide bodies live in a generated chunk (~240 kB of prose across every published guide,
 * `docs/generated/guides.generated.ts`, reached through `docs/bodies.ts`) that must never
 * enter the EAGER bundle — the Ask panel mounts on every authenticated page, so a static
 * import here would tax every page load for text that is needed only when a question
 * actually resolves to a section. `loadBodies()` below reaches it with a dynamic `import()`
 * instead of the module-level `import { getDocBody } from 'docs/bodies'` that every other
 * static importer uses, and caches the resulting promise so a second question in the same
 * session does not re-fetch the chunk.
 */
import { FEATURE_SWITCHES_OFF, type FeatureSwitches } from 'constants/featureSwitches'
import { customerDocsCatalogFor } from 'docs'
import { searchDocEntries } from 'search/scoring'

import { excerptForMatch } from './docSection'
import type { AskDocContext } from './provider'

/**
 * How many passages to ground an answer in.
 *
 * Small on purpose. Grounding is only useful if the passages are actually relevant; a
 * long tail of weak matches gives a model permission to answer from something that barely
 * mentioned the topic, which is how confidently-wrong help answers happen.
 */
const MAX_CONTEXT = 5

/**
 * Score below which a match is treated as noise.
 *
 * With the field weights in scoring.ts, a single term matching only an excerpt scores 2.
 * That is the "this word appears somewhere in the prose" case, and it is not grounds for
 * an answer — so the floor sits just above it.
 */
const MIN_SCORE = 2.5

/** `typeof import('docs/bodies')` — kept out of this module's own static graph; see file doc. */
type BodiesModule = typeof import('docs/bodies')

/**
 * The dynamic import's own module cache already dedupes repeat `import()` calls for the
 * same specifier, but holding the promise here means every call after the first resolves
 * synchronously against an already-settled promise rather than re-entering the module
 * loader machinery at all.
 */
let bodiesModulePromise: Promise<BodiesModule> | null = null

const loadBodies = (): Promise<BodiesModule> => {
  bodiesModulePromise ??= import('docs/bodies')
  return bodiesModulePromise
}

export const retrieveDocContext = async (
  question: string,
  limit = MAX_CONTEXT,
  // VRG-680 — the launch switches from `GET /features`. A guide gated on a switch that is off
  // is not in the corpus at all, and gated blocks inside other guides are blanked before a
  // section is sliced, so Ask can never ground an answer in them. Fails closed by default.
  switches: Partial<FeatureSwitches> = FEATURE_SWITCHES_OFF,
): Promise<AskDocContext[]> => {
  const matches = searchDocEntries(customerDocsCatalogFor(switches).docs, question, limit * 2)
    .filter(m => m.score >= MIN_SCORE)
    .slice(0, limit)

  // The chunk is only worth fetching if at least one match is heading-level — a data
  // question that matches no docs at all, or one whose matches are all page-level (the
  // query hit a title/tag, not a heading), never needs the guide bodies. Loaded once per
  // call rather than per match: a single question can produce several heading-level
  // matches out of the same guide (the very case that motivated this fix), and they
  // should share one chunk fetch.
  const bodiesModule = matches.some(m => m.heading) ? await loadBodies() : undefined
  const getDocBody = bodiesModule
    ? (doc: Parameters<BodiesModule['getDocBody']>[0]) => bodiesModule.getDocBody(doc, undefined, switches)
    : undefined

  return matches.map(m => ({
    slug: m.doc.slug,
    category: m.doc.category,
    title: m.doc.title,
    headingId: m.heading?.id,
    headingText: m.heading?.text,
    excerpt: getDocBody ? excerptForMatch(m, getDocBody) : m.doc.excerpt,
  }))
}
